<template>
  <div class="tw-flex tw-flex-col tw-gap-4" :data-testid="testid">
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-4">
      <span class="tw-text-sm tw-font-medium tw-text-sf-slate-dark-2">
        {{ label }}
      </span>
      <SfButtonDs
        v-if="adding"
        variant="secondary"
        size="sm"
        icon-before="plus"
        :aria-expanded="picking"
        :data-testid="`${testid}-add`"
        @click="picking = !picking"
      >
        {{ adding.label }}
      </SfButtonDs>
    </div>

    <AccessPicker
      v-if="adding && picking"
      :placeholder="adding.placeholder"
      :search="adding.search"
      @select="onSelect"
    />

    <SfInpageList v-if="items.length">
      <SfInpageListItem
        v-for="item in items"
        :key="item.key"
        :title="item.title"
        :item-id="item.key"
      >
        <template #icon>
          <span
            class="fa tw-text-sf-slate"
            :class="`fa-${icon}`"
            aria-hidden="true"
          />
        </template>
        <template v-if="item.subtitle" #subtitles>
          <SfInpageListItemSubtitle>
            {{ item.subtitle }}
          </SfInpageListItemSubtitle>
        </template>
        <template #right>
          <SfButtonDs
            v-if="item.removable"
            variant="secondary"
            size="sm"
            :aria-label="I18n.t('user.panel.remove_item', { name: item.title })"
            @click="emit('remove', item.key)"
          >
            {{ I18n.t('user.panel.remove') }}
          </SfButtonDs>
        </template>
      </SfInpageListItem>
    </SfInpageList>

    <p
      v-else
      class="
        tw-m-0
        tw-rounded
        tw-bg-sf-slate-subtle-1
        tw-py-10
        tw-text-center
        tw-text-sm
        tw-text-sf-slate-light-2
      "
    >
      {{ emptyLabel }}
    </p>
  </div>
</template>

<script setup lang="ts">
import type {
  AccessListAdding,
  AccessListIcon,
  AccessListItem,
  AutocompleteOption,
} from '@app/application/user-management/types'

import { ref } from 'vue'

import useI18n from '@app/shared/vue/composables/use-I18n'
import AccessPicker from '@app/application/user-management/components/user-panel/access-picker.vue'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfInpageList from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list.vue'
import SfInpageListItem from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item.vue'
import SfInpageListItemSubtitle from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item-subtitle.vue'

defineOptions({ name: 'AccessList' })

defineProps<{
  testid: string
  icon: AccessListIcon
  label: string
  emptyLabel: string
  items: AccessListItem[]
  adding?: AccessListAdding
}>()

const emit =
  defineEmits<{
    add: [option: AutocompleteOption]
    remove: [key: AccessListItem['key']]
  }>()

const I18n = useI18n()

const picking = ref(false)

function onSelect(option: AutocompleteOption): void {
  picking.value = false

  emit('add', option)
}
</script>
